import { useQuery } from "@tanstack/react-query";
import { keywordVolumeService, type KeywordVolumeSeries } from "@/services/KeywordVolumeService";
import type { Json } from "@/types/database";
import { setVolumeAnchor } from "@/components/clicks/metricDisplay";
import { isUrlFilterMultiEntry, type UrlActiveFilters } from "@/lib/url-active-filters";

/** Group By values that have a brand keyword behind them. */
export const KEYWORD_VOLUME_DIMS = new Set(["brand", "domain", "url"]);

/**
 * Countries of an include-filter on `country`.
 * ponytail: an exclude-filter is ignored (row shows all its geos); add when
 * someone filters Clicks by "everything but X" and expects volume to follow.
 */
export const includedCountries = (filters: UrlActiveFilters | undefined): string[] => {
  const f = filters?.country;
  if (!f || f.mode !== "include") return [];
  return isUrlFilterMultiEntry(f) ? f.values : [f.value];
};

/**
 * Keyword volume for the rows on screen: Map of row value -> series.
 * Called once per table (page level), not per cell. Empty map for other dims.
 */
export const useKeywordVolumes = (
  groupBy: string,
  values: string[],
  filters?: UrlActiveFilters,
) => {
  const countries = includedCountries(filters);
  const enabled = KEYWORD_VOLUME_DIMS.has(groupBy) && values.length > 0;
  const sorted = [...values].sort();
  return useQuery({
    queryKey: ["keywordVolumesForRows", groupBy, sorted, countries],
    // Fail-soft: a missing volume column must not take the Clicks table down
    // (e.g. before migration 20260929150000 is deployed).
    queryFn: () =>
      keywordVolumeService.getForRows(groupBy, sorted, countries).catch((e: unknown) => {
        console.warn("keyword volume unavailable:", e);
        return new Map<string, KeywordVolumeSeries>();
      }),
    enabled,
    staleTime: 1000 * 60 * 30, // monthly data
  });
};

/**
 * Keyword volume trend for the chart; fail-soft like the table column. The
 * period is resolved by the caller (same dates the Keitaro trend uses).
 */
export const useKeywordVolumeTrend = (
  from: string | null,
  to: string,
  filters: UrlActiveFilters | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ["keywordVolumeTrend", from, to, filters ?? {}],
    queryFn: () =>
      keywordVolumeService.getTrend(from, to, (filters ?? {}) as Json).catch((e: unknown) => {
        console.warn("keyword volume trend unavailable:", e);
        return [];
      }),
    enabled,
    staleTime: 1000 * 60 * 30,
  });

/**
 * Newest uploaded month, published to metricLabel() so kv_m1..kv_m12 read as
 * calendar months everywhere (table, picker, chart). Call once on the page.
 */
export const useVolumeAnchor = (): string | undefined => {
  const { data } = useQuery({
    queryKey: ["keywordVolumeAnchor"],
    queryFn: () => keywordVolumeService.getAnchor().catch(() => null),
    staleTime: 1000 * 60 * 30,
  });
  const anchor = data ?? undefined;
  setVolumeAnchor(anchor);
  return anchor;
};
